import { useCallback, useEffect, useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import type { SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import type { CustomView, GroupColors } from "../../datagrid/ColumnVisibilityModal";
import type {
	BackendAdapter,
	ColorGradingConfig,
	ColumnConfig,
	ColumnVisibility,
	FilterConfig,
	FilterValues,
	SortConfig,
} from "../../datagrid-configs/types";
import { mergeColumnOrder } from "../../datagrid-configs/types";
import { createSortArrows } from "../../discover/functions";
import { getColorForValue, getContrastTextColor } from "../../datagrid/colorGradingUtils";
import { TooltipComponent } from "ui-kit/src/TooltipComponent/TooltipComponent";

export interface UseOperationsManagementTableConfig<TRow extends Record<string, unknown>> {
	/** Column definitions */
	columns: ColumnConfig<TRow>[];
	/** Filter definitions */
	filters: FilterConfig[];
	/** Backend adapter for API calls */
	backendAdapter: BackendAdapter<TRow>;
	/** Storage key prefix for localStorage */
	storageKey: string;
	/** Initial column visibility overrides */
	initialColumnVisibility?: ColumnVisibility;
	/** Default page size */
	defaultPageSize?: number;
	/** Initial filter values on mount (avoids useEffect + extra fetch in ClubsTable) */
	initialFilterValues?: FilterValues;
	/** External group colors (overrides internal state when provided) */
	externalGroupColors?: GroupColors;
	/** External column order (overrides internal state when provided) */
	externalColumnOrder?: string[];
	/** External sort state (overrides internal state when provided) */
	externalSort?: SortConfig;
	/** External sort handler (overrides internal handler when provided) */
	externalHandleSort?: (newSort?: SortConfig) => void;
	/** External color grading config (overrides internal state when provided) */
	externalColorGradingConfig?: ColorGradingConfig;
	/** External color grading handler (overrides internal handler when provided) */
	externalHandleColorGradingChange?: (config: ColorGradingConfig) => void;
	/** List of percentage column keys for color grading */
	percentageColumns?: Array<{ key: string; label: string; group: string }>;
	/** External column widths (overrides internal state when provided) */
	externalColumnWidths?: Record<string, number>;
	/** External column widths handler (overrides internal handler when provided) */
	externalHandleColumnWidthsChange?: (widths: Record<string, number>) => void;
}

export interface UseOperationsManagementTableReturn<TRow extends Record<string, unknown>> {
	// Pagination
	pagination: PaginationState;
	setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;

	// Sorting
	sort: SortConfig | undefined;
	handleSort: (newSort?: SortConfig) => void;

	// Filters
	filterValues: FilterValues;
	filtersCollapsed: boolean;
	handleFilterChange: (values: FilterValues | ((previousValues: FilterValues) => FilterValues)) => void;
	handleClearFilters: () => void;
	handleToggleFiltersCollapse: () => void;

	// Column visibility
	visibleColumns: ColumnVisibility;
	defaultVisibility: ColumnVisibility;
	columnOrder: string[];
	defaultColumnOrder: string[];
	showColumnModal: boolean;
	setShowColumnModal: React.Dispatch<React.SetStateAction<boolean>>;
	handleVisibilityChange: (visibility: ColumnVisibility, order?: string[], colors?: GroupColors) => void;

	// Custom views
	customViews: CustomView[];
	selectedCustomViewId: string | null;
	selectedViewName: string | null;
	handleCustomViewsChange: (views: CustomView[], selectedId: string | null) => void;

	// Group colors
	groupColors: GroupColors;

	// Color grading
	colorGradingConfig: ColorGradingConfig;
	showColorGradingModal: boolean;
	setShowColorGradingModal: React.Dispatch<React.SetStateAction<boolean>>;
	handleColorGradingChange: (config: ColorGradingConfig) => void;

	// Computed columns for DatagridV2
	simpleColumns: SimpleColumn<TRow>[];
	columnGroups: Record<string, Array<{ key: string; label: string }>>;
	columnTotals: Record<string, number>;

	// API helpers (for when backend is ready)
	apiFilters: ReturnType<BackendAdapter<TRow>["buildFilters"]>;
	apiSort: { field: string; direction: string } | undefined;
	apiColumns: string[];

	// Column widths
	columnWidths: Record<string, number>;
	handleColumnWidthsChange: (widths: Record<string, number>) => void;
}

/**
 * Custom hook for Operations Management table state management.
 * Handles pagination, filters, column visibility, custom views, and localStorage persistence.
 */
export function useOperationsManagementTable<TRow extends Record<string, unknown>>(
	config: UseOperationsManagementTableConfig<TRow>,
	data: TRow[]
): UseOperationsManagementTableReturn<TRow> {
	const {
		columns,
		filters: _filters,
		backendAdapter,
		storageKey,
		initialColumnVisibility = {},
		defaultPageSize = 25,
		initialFilterValues,
		externalGroupColors,
		externalColumnOrder,
		externalSort,
		externalHandleSort,
		externalColorGradingConfig,
		externalHandleColorGradingChange,
		percentageColumns = [],
		externalColumnWidths,
		externalHandleColumnWidthsChange,
	} = config;

	// Helper for storage keys
	const getKey = useCallback((suffix: string) => `${storageKey}_${suffix}`, [storageKey]);

	// Pagination state
	const [pagination, setPagination] = useState<PaginationState>(() => {
		const stored = localStorage.getItem(`${storageKey}_pageSize`);
		if (stored !== null) {
			const parsed = Number(stored);
			if (Number.isFinite(parsed) && parsed > 0) {
				return { pageIndex: 0, pageSize: parsed };
			}
		}

		return { pageIndex: 0, pageSize: defaultPageSize };
	});

	// Sorting state
	const [sort, setSort] = useState<SortConfig | undefined>(undefined);

	// Column visibility modal state
	const [showColumnModal, setShowColumnModal] = useState(false);

	// Color grading modal state
	const [showColorGradingModal, setShowColorGradingModal] = useState(false);

	// Color grading config state
	const [colorGradingConfig, setColorGradingConfig] = useState<ColorGradingConfig>(() => {
		const stored = localStorage.getItem(getKey("colorGrading"));
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				return {};
			}
		}
		return {};
	});

	// Filter state (initialFilterValues avoids post-mount useEffect and extra fetch)
	const [filterValues, setFilterValues] = useState<FilterValues>(() => initialFilterValues ?? {});
	const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
		const stored = localStorage.getItem(getKey("filtersCollapsed"));
		return stored === "true";
	});

	// Custom views state
	const [customViews, setCustomViews] = useState<CustomView[]>(() => {
		const stored = localStorage.getItem(getKey("customViews"));
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				return [];
			}
		}
		return [];
	});

	// Selected custom view ID - prioritize stored selection, then fall back to default view
	const [selectedCustomViewId, setSelectedCustomViewId] = useState<string | null>(() => {
		// First check for explicitly selected view
		const stored = localStorage.getItem(getKey("selectedView"));
		if (stored) {
			return stored;
		}

		// Fall back to default view if one exists
		const storedViews = localStorage.getItem(getKey("customViews"));
		if (storedViews) {
			try {
				const views: CustomView[] = JSON.parse(storedViews);
				const defaultView = views.find((v) => v.isDefault);
				if (defaultView) {
					return defaultView.id;
				}
			} catch {
				// Ignore parse errors
			}
		}

		return null;
	});

	// Group colors state
	const [groupColors, setGroupColors] = useState<GroupColors>(() => {
		const storedViews = localStorage.getItem(getKey("customViews"));
		let views: CustomView[] = [];
		if (storedViews) {
			try {
				views = JSON.parse(storedViews);
			} catch {
				// Ignore parse errors
			}
		}

		// First check for explicitly selected view
		const storedViewId = localStorage.getItem(getKey("selectedView"));
		if (storedViewId) {
			const selectedView = views.find((v) => v.id === storedViewId);
			if (selectedView?.groupColors) {
				return selectedView.groupColors;
			}
		}

		// Fall back to default view if one exists
		const defaultView = views.find((v) => v.isDefault);
		if (defaultView?.groupColors) {
			return defaultView.groupColors;
		}

		// Fall back to stored group colors
		const stored = localStorage.getItem(getKey("groupColors"));
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				return {};
			}
		}
		return {};
	});

	// Build default visibility from column config
	const defaultVisibility = useMemo(() => {
		const defaults: ColumnVisibility = {};
		columns.forEach((col) => {
			defaults[String(col.key)] = col.defaultVisible !== false;
		});
		return { ...defaults, ...initialColumnVisibility };
	}, [columns, initialColumnVisibility]);

	// Default column order
	const defaultColumnOrder = useMemo(() => {
		return columns.map((col) => String(col.key));
	}, [columns]);

	// Column visibility state
	const [visibleColumns, setVisibleColumns] = useState<ColumnVisibility>(() => {
		const storedViews = localStorage.getItem(getKey("customViews"));
		let views: CustomView[] = [];
		if (storedViews) {
			try {
				views = JSON.parse(storedViews);
			} catch {
				// Ignore parse errors
			}
		}

		// First check for explicitly selected view
		const storedViewId = localStorage.getItem(getKey("selectedView"));
		if (storedViewId) {
			const selectedView = views.find((v) => v.id === storedViewId);
			if (selectedView) {
				return selectedView.visibility;
			}
		}

		// Fall back to default view if one exists
		const defaultView = views.find((v) => v.isDefault);
		if (defaultView) {
			return defaultView.visibility;
		}

		// Fall back to stored visibility
		const stored = localStorage.getItem(getKey("visibility"));
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				// fall through
			}
		}

		// Build defaults inline
		const defaults: ColumnVisibility = {};
		columns.forEach((col) => {
			defaults[String(col.key)] = col.defaultVisible !== false;
		});
		return { ...defaults, ...initialColumnVisibility };
	});

	// Column order state
	const [columnOrder, setColumnOrder] = useState<string[]>(() => {
		const defaultOrder = columns.map((col) => String(col.key));
		const stored = localStorage.getItem(getKey("order"));
		if (stored) {
			try {
				return mergeColumnOrder(defaultOrder, JSON.parse(stored));
			} catch {
				// Invalid JSON, use defaults
			}
		}
		return defaultOrder;
	});

	// Column widths state
	const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
		const stored = localStorage.getItem(getKey("columnWidths"));
		if (stored) {
			try {
				return JSON.parse(stored);
			} catch {
				return {};
			}
		}
		return {};
	});

	// Persist to localStorage
	useEffect(() => {
		localStorage.setItem(getKey("visibility"), JSON.stringify(visibleColumns));
	}, [visibleColumns, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("order"), JSON.stringify(columnOrder));
	}, [columnOrder, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("customViews"), JSON.stringify(customViews));
	}, [customViews, getKey]);

	useEffect(() => {
		if (selectedCustomViewId) {
			localStorage.setItem(getKey("selectedView"), selectedCustomViewId);
		} else {
			localStorage.removeItem(getKey("selectedView"));
		}
	}, [selectedCustomViewId, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("filtersCollapsed"), String(filtersCollapsed));
	}, [filtersCollapsed, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("groupColors"), JSON.stringify(groupColors));
	}, [groupColors, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("colorGrading"), JSON.stringify(colorGradingConfig));
	}, [colorGradingConfig, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("columnWidths"), JSON.stringify(columnWidths));
	}, [columnWidths, getKey]);

	useEffect(() => {
		localStorage.setItem(getKey("pageSize"), String(pagination.pageSize));
	}, [pagination.pageSize, getKey]);
	// Handlers (ensure at least one column stays visible)
	const handleVisibilityChange = useCallback(
		(visibility: ColumnVisibility, order?: string[], colors?: GroupColors) => {
			const visibleCount = Object.values(visibility).filter((v) => v !== false).length;
			let appliedVisibility = visibility;
			if (visibleCount < 1 && columns.length > 0) {
				const firstKey = String(columns[0].key);
				appliedVisibility = { ...visibility, [firstKey]: true };
			}
			setVisibleColumns(appliedVisibility);
			if (order) {
				setColumnOrder(order);
			}
			if (colors !== undefined) {
				setGroupColors(colors);
			}
		},
		[columns]
	);

	const handleCustomViewsChange = useCallback(
		(views: CustomView[], selectedId: string | null) => {
			setCustomViews(views);
			setSelectedCustomViewId(selectedId);

			if (selectedId) {
				const selectedView = views.find((v) => v.id === selectedId);
				if (selectedView) {
					let visibility = selectedView.visibility;
					const visibleCount = Object.values(visibility).filter((v) => v !== false).length;
					if (visibleCount < 1 && columns.length > 0) {
						visibility = { ...visibility, [String(columns[0].key)]: true };
					}
					setVisibleColumns(visibility);
					if (selectedView.groupColors) {
						setGroupColors(selectedView.groupColors);
					}
				}
			}
		},
		[columns]
	);

	const handleFilterChange = useCallback((values: FilterValues | ((previousValues: FilterValues) => FilterValues)) => {
		setFilterValues(values);
		setPagination((prev: PaginationState) => ({ ...prev, pageIndex: 0 }));
	}, []);

	const handleClearFilters = useCallback(() => {
		setFilterValues({});
		setPagination((prev: PaginationState) => ({ ...prev, pageIndex: 0 }));
	}, []);

	const handleToggleFiltersCollapse = useCallback(() => {
		setFiltersCollapsed((prev) => !prev);
	}, []);

	// Sort handler - for use with createSortArrows (toggles or clears)
	const handleSort = useCallback((newSort?: { field: string; direction: string }) => {
		setSort(newSort);
		// Reset to first page when sorting changes
		setPagination((prev) => ({ ...prev, pageIndex: 0 }));
	}, []);

	// Color grading handler
	const handleColorGradingChange = useCallback((config: ColorGradingConfig) => {
		setColorGradingConfig(config);
	}, []);

	// Column widths handler
	const handleColumnWidthsChange = useCallback((widths: Record<string, number>) => {
		setColumnWidths(widths);
	}, []);

	// API helpers
	const apiFilters = useMemo(() => {
		return backendAdapter.buildFilters(filterValues);
	}, [filterValues, backendAdapter]);

	const apiSort = useMemo(() => {
		return backendAdapter.buildSort?.(sort) ?? sort;
	}, [sort, backendAdapter]);

	const apiColumns = useMemo(() => {
		const fields = new Set<string>();
		columns.forEach((col) => {
			const colKey = String(col.key);
			if (visibleColumns[colKey] !== false) {
				if (col.requiredFields) {
					col.requiredFields.forEach((field) => fields.add(field));
				} else {
					fields.add(colKey);
				}
			}
		});
		return Array.from(fields);
	}, [visibleColumns, columns]);

	// Calculate totals for summable columns
	const columnTotals = useMemo(() => {
		const totals: Record<string, number> = {};
		const summableColumns = columns.filter((col) => col.summable);
		summableColumns.forEach((col) => {
			const key = String(col.key);
			let sum = 0;
			data.forEach((row) => {
				const value = row[key];
				if (typeof value === "number" && !Number.isNaN(value)) {
					sum += value;
				}
			});
			totals[key] = sum;
		});
		return totals;
	}, [data, columns]);

	// Subscription breakdown from current page (for Active Subscription footer tooltip)
	const subscriptionBreakdownFromData = useMemo(() => {
		let subscriptionsSold = 0;
		let bronzeSold = 0;
		let silverSold = 0;
		let goldSold = 0;
		let platinumSold = 0;
		let otherSold = 0;
		data.forEach((row) => {
			subscriptionsSold += (row.subscriptionsSold as number) ?? 0;
			bronzeSold += (row.bronzeSold as number) ?? 0;
			silverSold += (row.silverSold as number) ?? 0;
			goldSold += (row.goldSold as number) ?? 0;
			platinumSold += (row.platinumSold as number) ?? 0;
			otherSold += (row.otherSold as number) ?? 0;
		});
		return { subscriptionsSold, bronzeSold, silverSold, goldSold, platinumSold, otherSold };
	}, [data]);

	// Create a map from column key to group
	const columnToGroup = useMemo(() => {
		const map: Record<string, string> = {};
		columns.forEach((col) => {
			map[String(col.key)] = col.group || "General";
		});
		return map;
	}, [columns]);

	// Use external values when provided, otherwise use internal state
	const effectiveGroupColors = externalGroupColors ?? groupColors;
	const effectiveColumnOrder = externalColumnOrder ?? columnOrder;
	const effectiveSort = externalSort !== undefined ? externalSort : sort;
	const effectiveHandleSort = externalHandleSort ?? handleSort;
	const effectiveColorGradingConfig = externalColorGradingConfig ?? colorGradingConfig;
	const effectiveHandleColorGradingChange = externalHandleColorGradingChange ?? handleColorGradingChange;
	const effectiveColumnWidths = externalColumnWidths ?? columnWidths;
	const effectiveHandleColumnWidthsChange = externalHandleColumnWidthsChange ?? handleColumnWidthsChange;


	// Convert ColumnConfig to SimpleColumn format for DatagridV2
	const simpleColumns: SimpleColumn<TRow>[] = useMemo(() => {
		const columnMap = new Map(columns.map((col) => [String(col.key), col]));
		const mergedOrder = mergeColumnOrder(
			columns.map((col) => String(col.key)),
			effectiveColumnOrder,
		);
		const orderedColumns = mergedOrder.map((key) => columnMap.get(key)).filter((col): col is ColumnConfig<TRow> => col !== undefined);

		return orderedColumns.map((col) => {
			const colKey = String(col.key);
			const group = columnToGroup[colKey];
			const groupBgColor = effectiveGroupColors[group];
			const groupTextColor = groupBgColor ? getContrastTextColor(groupBgColor) : undefined;

			// Check if this is a percentage column with color grading enabled
			const isPercentageColumn = percentageColumns.some((pc) => pc.key === colKey);
			const colorGradingForColumn = isPercentageColumn ? effectiveColorGradingConfig[colKey] : undefined;

			const cellRenderer = col.cell
				? (value: TRow[keyof TRow], row: TRow) => {
						const content = col.cell!(value, row);

						// Apply color grading for percentage columns if enabled
						if (colorGradingForColumn?.enabled && typeof value === "number") {
							const gradingColor = getColorForValue(value, colorGradingForColumn);
							if (gradingColor) {
								const gradingTextColor = getContrastTextColor(gradingColor);
								return (
									<div
										className="operationsManagementSummaryTableV3-coloredCell operationsManagementSummaryTableV3-gradedCell"
										style={{ backgroundColor: gradingColor, color: gradingTextColor }}
									>
										{content}
									</div>
								);
							}
						}

						// Fall back to group color
						if (groupBgColor) {
							return (
								<div className="operationsManagementSummaryTableV3-coloredCell" style={{ backgroundColor: groupBgColor, color: groupTextColor }}>
									{content}
								</div>
							);
						}
						return content;
					}
				: groupBgColor
					? (value: TRow[keyof TRow]) => (
							<div className="operationsManagementSummaryTableV3-coloredCell" style={{ backgroundColor: groupBgColor, color: groupTextColor }}>
								{value != null ? String(value) : "-"}
							</div>
						)
					: undefined;

			const headerLabel = col.headerTitle ? (
				<TooltipComponent title={col.headerTitle} delay={500}>
					<span className="tableHeader-label">{col.label}</span>
				</TooltipComponent>
			) : (
				<span className="tableHeader-label">{col.label}</span>
			);

			const headerRenderer = () => {
				const sortField = col.sortField || colKey;
				const sortArrows = col.sortable ? createSortArrows(sortField, effectiveSort, effectiveHandleSort) : null;

				if (groupBgColor) {
					return (
						<div
							className="tableHeader operationsManagementSummaryTableV3-coloredHeader"
							style={{ backgroundColor: groupBgColor, color: groupTextColor }}
						>
							{headerLabel}
							<span className="tableHeader-sortArrows">{sortArrows}</span>
						</div>
					);
				}

				// Wrap in tableHeader div to apply sort arrow styles from discoverPage.css
				return (
					<div className="tableHeader">
						{headerLabel}
						<span className="tableHeader-sortArrows">{sortArrows}</span>
					</div>
				);
			};

			const footerContent =
				col.summable && columnTotals[colKey] !== undefined ? (
					<div
						className="operationsManagementSummaryTableV3-footerValue"
						style={groupBgColor ? { backgroundColor: groupBgColor, color: groupTextColor } : undefined}
					>
						{colKey === "subscriptionsSold" ? (
							<span className="operationsManagementSummaryTableV3-subscriptionsSoldTrigger">
								<TooltipComponent
									title={`Bronze: ${subscriptionBreakdownFromData.bronzeSold}, Silver: ${subscriptionBreakdownFromData.silverSold}, Gold: ${subscriptionBreakdownFromData.goldSold}, Platinum: ${subscriptionBreakdownFromData.platinumSold}, Other: ${subscriptionBreakdownFromData.otherSold}`}
									delay={500}
								>
									{columnTotals[colKey].toLocaleString()}
								</TooltipComponent>
							</span>
						) : (
							columnTotals[colKey].toLocaleString()
						)}
					</div>
				) : colKey === "continent" ? (
					<strong>Total</strong>
				) : (
					""
				);

			return {
				key: col.key,
				id: colKey,
				header: headerRenderer,
				cell: cellRenderer,
				footer: footerContent,
				width: effectiveColumnWidths[colKey],
				minWidth: col.minWidth ?? col.width ?? 50,
				maxWidth: col.maxWidth,
			};
		});
	}, [
		effectiveColumnOrder,
		columnToGroup,
		effectiveGroupColors,
		columnTotals,
		columns,
		effectiveSort,
		effectiveHandleSort,
		percentageColumns,
		effectiveColorGradingConfig,
		effectiveColumnWidths,
		subscriptionBreakdownFromData,
	]);

	// Group columns by category for the visibility modal
	const columnGroups = useMemo(() => {
		const groups: Record<string, Array<{ key: string; label: string }>> = {};
		columns.forEach((col) => {
			const group = col.group || "General";
			if (!groups[group]) {
				groups[group] = [];
			}
			groups[group].push({
				key: String(col.key),
				label: col.label,
			});
		});
		return groups;
	}, [columns]);

	// Get the name of the selected custom view
	const selectedViewName = useMemo(() => {
		if (!selectedCustomViewId) return null;
		const view = customViews.find((v) => v.id === selectedCustomViewId);
		return view?.name || null;
	}, [selectedCustomViewId, customViews]);

	return {
		// Pagination
		pagination,
		setPagination,

		// Sorting
		sort,
		handleSort,

		// Filters
		filterValues,
		filtersCollapsed,
		handleFilterChange,
		handleClearFilters,
		handleToggleFiltersCollapse,

		// Column visibility
		visibleColumns,
		defaultVisibility,
		columnOrder,
		defaultColumnOrder,
		showColumnModal,
		setShowColumnModal,
		handleVisibilityChange,

		// Custom views
		customViews,
		selectedCustomViewId,
		selectedViewName,
		handleCustomViewsChange,

		// Group colors
		groupColors,

		// Color grading
		colorGradingConfig: effectiveColorGradingConfig,
		showColorGradingModal,
		setShowColorGradingModal,
		handleColorGradingChange: effectiveHandleColorGradingChange,

		// Computed columns
		simpleColumns,
		columnGroups,
		columnTotals,

		// API helpers
		apiFilters,
		apiSort,
		apiColumns,

		// Column widths
		columnWidths: effectiveColumnWidths,
		handleColumnWidthsChange: effectiveHandleColumnWidthsChange,
	};
}
